import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Loader2, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Textarea, Select, SectionTitle } from "@/components/FormBits";
import ChipGroup from "@/components/ChipGroup";
import { api } from "@/lib/api";

const PROJECT_STATUSES = ["Completed", "In progress", "Upcoming"];
const SUB_STATUSES = ["Draft", "Draft Photo Submission", "Submitted", "In Review", "Written", "Published", "Archived"];

export default function AdminProjectEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/admin/projects/${id}`), api.get(`/admin/settings`)])
      .then(([pr, sr]) => {
        setP(pr.data);
        setSettings(sr.data);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const set = (patch) => setP((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/projects/${id}`, p);
      setP(data);
      toast.success("Project saved");
      navigate(`/admin/projects/${id}`);
    } catch (e) {
      const d = e?.response?.data?.detail || "Save failed";
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !p || !settings) {
    return (
      <Layout>
        <div className="py-20 text-center text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading…
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 rcw-mobile-pad-bottom">
        <Link to={`/admin/projects/${id}`} className="rcw-btn-ghost text-sm mb-3">
          <ArrowLeft className="h-4 w-4" /> Back to project
        </Link>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="rcw-label">Editing project</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 mt-1">
              {p.project_name || "Untitled"}
            </h1>
          </div>
        </div>

        {/* Submission status (admin-only on edit page) */}
        <Card title="Admin status">
          <Field label="Submission status">
            <Select
              options={SUB_STATUSES}
              value={p.status || ""}
              onChange={(e) => set({ status: e.target.value })}
              data-testid="edit-status"
            />
          </Field>
        </Card>

        <Card title="Submitted by">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name"><Input value={p.submitter_name || ""} onChange={(e) => set({ submitter_name: e.target.value })} data-testid="edit-submitter-name" /></Field>
            <Field label="Email"><Input type="email" value={p.submitter_email || ""} onChange={(e) => set({ submitter_email: e.target.value })} data-testid="edit-submitter-email" /></Field>
          </div>
        </Card>

        <Card title="Project details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Project name"><Input value={p.project_name || ""} onChange={(e) => set({ project_name: e.target.value })} data-testid="edit-project-name" /></Field>
            <Field label="Short title"><Input value={p.short_title || ""} onChange={(e) => set({ short_title: e.target.value })} /></Field>
            <Field label="Client"><Input value={p.client_name || ""} onChange={(e) => set({ client_name: e.target.value })} data-testid="edit-client" /></Field>
            <Field label="Location"><Input value={p.location || ""} onChange={(e) => set({ location: e.target.value })} /></Field>
            <Field label="Year completed"><Input value={p.year_completed || ""} onChange={(e) => set({ year_completed: e.target.value })} /></Field>
            <Field label="Project value"><Input value={p.project_value || ""} onChange={(e) => set({ project_value: e.target.value })} /></Field>
            <Field label="Start date"><Input type="date" value={p.project_start_date || ""} onChange={(e) => set({ project_start_date: e.target.value })} /></Field>
            <Field label="End date"><Input type="date" value={p.project_end_date || ""} onChange={(e) => set({ project_end_date: e.target.value })} /></Field>
            <Field label="Project status"><Select options={PROJECT_STATUSES} value={p.project_status || ""} onChange={(e) => set({ project_status: e.target.value })} /></Field>
          </div>
          <Field label="Categories">
            <ChipGroup options={settings.categories} value={p.categories || []} onChange={(v) => set({ categories: v })} allowAdd={false} testIdPrefix="edit-cat" />
          </Field>
          <Field label="Additional category info"><Textarea value={p.additional_category_info || ""} onChange={(e) => set({ additional_category_info: e.target.value })} /></Field>
        </Card>

        <Card title="Project summary">
          <Field label="One sentence"><Input value={p.one_sentence || ""} onChange={(e) => set({ one_sentence: e.target.value })} data-testid="edit-one-sentence" /></Field>
          <Field label="Brief description"><Textarea value={p.brief_description || ""} onChange={(e) => set({ brief_description: e.target.value })} /></Field>
          <Field label="Why was the work needed?"><Textarea value={p.why_needed || ""} onChange={(e) => set({ why_needed: e.target.value })} /></Field>
          <Field label="Who was affected">
            <ChipGroup options={settings.affected} value={p.who_affected || []} onChange={(v) => set({ who_affected: v })} allowAdd={false} testIdPrefix="edit-affected" />
          </Field>
          <Field label="Additional summary info"><Textarea value={p.additional_summary_info || ""} onChange={(e) => set({ additional_summary_info: e.target.value })} /></Field>
        </Card>

        <Card title="The challenge">
          <Field label="Challenges">
            <ChipGroup options={settings.challenges} value={p.challenges || []} onChange={(v) => set({ challenges: v })} allowAdd={false} testIdPrefix="edit-chal" />
          </Field>
          <Field label="Main challenge"><Textarea value={p.main_challenge_text || ""} onChange={(e) => set({ main_challenge_text: e.target.value })} /></Field>
          <Field label="Other details"><Textarea value={p.other_challenge_text || ""} onChange={(e) => set({ other_challenge_text: e.target.value })} /></Field>
        </Card>

        <Card title="The solution">
          <Field label="Solutions">
            <ChipGroup options={settings.solutions} value={p.solutions || []} onChange={(v) => set({ solutions: v })} allowAdd={false} testIdPrefix="edit-sol" />
          </Field>
          <Field label="Works carried out"><Textarea value={p.works_carried_out || ""} onChange={(e) => set({ works_carried_out: e.target.value })} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Materials used"><Textarea value={p.materials_used || ""} onChange={(e) => set({ materials_used: e.target.value })} /></Field>
            <Field label="Specialist methods"><Textarea value={p.specialist_methods || ""} onChange={(e) => set({ specialist_methods: e.target.value })} /></Field>
            <Field label="Temporary works"><Textarea value={p.temporary_works || ""} onChange={(e) => set({ temporary_works: e.target.value })} /></Field>
            <Field label="Environmental controls"><Textarea value={p.environmental_controls || ""} onChange={(e) => set({ environmental_controls: e.target.value })} /></Field>
          </div>
          <Field label="Extra solution"><Textarea value={p.extra_solution || ""} onChange={(e) => set({ extra_solution: e.target.value })} /></Field>
        </Card>

        <Card title="The outcome">
          <Field label="Outcomes">
            <ChipGroup options={settings.outcomes} value={p.outcomes || []} onChange={(v) => set({ outcomes: v })} allowAdd={false} testIdPrefix="edit-out" />
          </Field>
          <Field label="Main result"><Textarea value={p.main_result || ""} onChange={(e) => set({ main_result: e.target.value })} /></Field>
          <Field label="Client / public benefit"><Textarea value={p.client_public_benefit || ""} onChange={(e) => set({ client_public_benefit: e.target.value })} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Delivered safely"><Textarea value={p.delivered_safely || ""} onChange={(e) => set({ delivered_safely: e.target.value })} /></Field>
            <Field label="Delivered on time"><Textarea value={p.delivered_on_time || ""} onChange={(e) => set({ delivered_on_time: e.target.value })} /></Field>
          </div>
          <Field label="Extra"><Textarea value={p.extra_outcome || ""} onChange={(e) => set({ extra_outcome: e.target.value })} /></Field>
        </Card>

        <Card title="Key facts">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ["duration", "Length of project"], ["area", "Area"],
              ["materials_summary", "Materials summary"], ["tonnes", "Tonnes"],
              ["assets_count", "Assets installed/replaced"], ["operatives", "Operatives"],
              ["plant_used", "Plant used"], ["traffic_management", "Traffic management"],
              ["environmental_measures", "Environmental measures"],
              ["useful_numbers", "Useful numbers"], ["awards", "Awards / feedback"],
            ].map(([k, lab]) => (
              <Field key={k} label={lab}>
                <Input value={p[k] || ""} onChange={(e) => set({ [k]: e.target.value })} />
              </Field>
            ))}
          </div>
          <CustomFactsEditor facts={p.custom_facts || []} onChange={(facts) => set({ custom_facts: facts })} />
        </Card>

        <Card title="Final notes & approval">
          <Field label="Most proud of"><Textarea value={p.proud_of || ""} onChange={(e) => set({ proud_of: e.target.value })} /></Field>
          <Field label="Unusual / impressive"><Textarea value={p.unusual_impressive || ""} onChange={(e) => set({ unusual_impressive: e.target.value })} /></Field>
          <Field label="Avoid saying publicly"><Textarea value={p.avoid_publicly || ""} onChange={(e) => set({ avoid_publicly: e.target.value })} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Can be used publicly"><Select options={["Yes", "No", "Check with client first"]} value={p.public_use || ""} onChange={(e) => set({ public_use: e.target.value })} /></Field>
            <Field label="Client approval needed"><Select options={["Yes", "No", "Unsure"]} value={p.client_approval || ""} onChange={(e) => set({ client_approval: e.target.value })} /></Field>
          </div>
        </Card>

        <Card title="Internal notes (admin only)">
          <Textarea value={p.internal_notes || ""} onChange={(e) => set({ internal_notes: e.target.value })} data-testid="edit-internal-notes" placeholder="Admin only notes…" />
        </Card>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-end gap-2">
          <Link to={`/admin/projects/${id}`} className="rcw-btn-ghost text-sm">
            <X className="h-4 w-4" /> Cancel
          </Link>
          <button onClick={save} disabled={saving} className="rcw-btn-amber" data-testid="edit-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </button>
        </div>
      </div>
    </Layout>
  );
}

function Card({ title, children }) {
  return (
    <div className="rcw-card p-5 sm:p-7 mt-5 space-y-4">
      <SectionTitle eyebrow={title}>{title}</SectionTitle>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function CustomFactsEditor({ facts, onChange }) {
  const add = () => onChange([...facts, { id: crypto.randomUUID(), label: "", value: "" }]);
  const upd = (id, patch) => onChange(facts.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const rem = (id) => onChange(facts.filter((f) => f.id !== id));
  return (
    <div className="pt-2">
      <div className="rcw-label mb-2">Custom facts</div>
      <div className="space-y-2">
        {facts.map((f) => (
          <div key={f.id} className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
            <Input value={f.label} onChange={(e) => upd(f.id, { label: e.target.value })} placeholder="Label" />
            <Input value={f.value} onChange={(e) => upd(f.id, { value: e.target.value })} placeholder="Value" />
            <button type="button" onClick={() => rem(f.id)} className="rcw-btn-ghost h-12 sm:h-auto" aria-label="Remove fact">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 rcw-btn-ghost text-sm">
        <Plus className="h-4 w-4" /> Add custom fact
      </button>
    </div>
  );
}
